/**
 * 숫자 포맷팅 유틸리티 함수들
 */

/**
 * 숫자에 천단위 콤마를 추가하는 함수
 * @param {number|string} num - 포맷팅할 숫자
 * @returns {string} 천단위 콤마가 포함된 문자열
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') {
    return '0';
  }
  
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * 숫자를 포맷팅하여 정수는 천단위 콤마로, 소수는 한 자리까지 표시
 * @param {number|string} value - 포맷팅할 값
 * @returns {string} 포맷팅된 문자열
 */
export const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  // 문자열이고 숫자로 변환할 수 없으면 그대로 반환
  if (typeof value === 'string') {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return value; // 문자열 그대로 반환 (예: "RACK-001", "정상")
    }
    value = numValue; // 숫자로 변환 가능하면 변환
  }
  
  const numValue = value;
  
  // 정수인 경우 천단위 콤마만 추가
  if (Number.isInteger(numValue)) {
    return formatNumber(numValue);
  }
  
  // 소수인 경우 한 자리까지 표시하고 천단위 콤마 추가
  const fixedValue = numValue.toFixed(1);
  return formatNumber(parseFloat(fixedValue));
};

/**
 * 한국어 로케일을 사용한 숫자 포맷팅 (천단위 콤마 포함)
 * @param {number|string} num - 포맷팅할 숫자
 * @returns {string} 포맷팅된 문자열
 */
export const formatNumberKorean = (num) => {
  if (num === null || num === undefined || num === '') {
    return '0';
  }

  const numValue = typeof num === 'string' ? parseFloat(num) : num;

  if (isNaN(numValue)) {
    return '0';
  }

  return numValue.toLocaleString('ko-KR');
};

/**
 * 지정된 소수점 자리수로 숫자를 포맷팅하는 함수
 * @param {number|string} value - 포맷팅할 값
 * @param {number|null} decimalPlaces - 소수점 자리수 (null이면 자동)
 * @returns {string} 포맷팅된 문자열
 */
export const formatValueWithDecimals = (value, decimalPlaces = null) => {
  // null, undefined, 빈 문자열 처리
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  // 문자열이고 숫자로 변환할 수 없으면 그대로 반환
  if (typeof value === 'string') {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return value; // 문자열 그대로 반환 (예: "RACK-001", "정상")
    }
    value = numValue; // 숫자로 변환 가능하면 변환
  }

  const numValue = value;

  // decimalPlaces가 null이거나 undefined면 기존 formatValue 로직 사용
  if (decimalPlaces === null || decimalPlaces === undefined) {
    return formatValue(value);
  }

  // 지정된 소수점 자리수로 포맷
  const formatted = numValue.toFixed(decimalPlaces);

  // 천단위 콤마 추가
  // toFixed는 문자열을 반환하므로, 정수부와 소수부를 분리하여 처리
  const parts = formatted.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return parts.join('.');
}; 